import React from 'react';
import { reduxForm } from 'redux-form';
import { useSelector } from 'react-redux';
import { compose, withProps } from 'recompose';
import { onEnter } from 'spark-util/keyboardEvents';
import { useGridContext } from './gridContext';
import { filterHomePageGrid } from '../state/gridActions';
import { getFilter } from '../state/gridSelectors';
import { createLegacyAutoSearchOnChange } from './filterBuilderLegacyAutoSearch';

/** Header-only keys for submit-only FilterBuilder (admin advanced search). */
export const SUBMIT_ONLY_HEADER_FILTER_KEYS = ['study', 'searchParam', 'distributionDate'];

const stopEnterEvent = onEnter((event) => {
	event.preventDefault();
	event.stopPropagation();
});

const FilterBuilderForm = ({ children }) => {
	return <div onKeyPress={stopEnterEvent}>{children}</div>;
};

/** Legacy grids: form values fully replace the grid filter. */
const onSubmitReplace = async (values, dispatch, { gridName }) => {
	dispatch(filterHomePageGrid(gridName, values));
};

/**
 * Submit-only grids: merge form values onto the current grid filter so column
 * filters (statusMGMT, snTitle, …) are never wiped by a form-only submit.
 */
export const onSubmitMerge = (values, dispatch, { gridName }) => {
	dispatch((innerDispatch, getState) => {
		const currentFilter = getFilter(getState(), gridName) || {};
		innerDispatch(filterHomePageGrid(gridName, { ...currentFilter, ...values }));
	});
};

export const pickHeaderFilterValues = (filter = {}) =>
	SUBMIT_ONLY_HEADER_FILTER_KEYS.reduce((acc, key) => {
		if (filter[key] !== undefined) {
			acc[key] = filter[key];
		}
		return acc;
	}, {});

const createFilterBuilderInner = (disableAutoSearch) =>
	compose(
		withProps(({ gridName }) => ({ form: `${gridName}_advancedSearch` })),
		reduxForm({
			...(disableAutoSearch
				? {}
				: {
						onChange: createLegacyAutoSearchOnChange(),
				  }),
			onSubmit: disableAutoSearch ? onSubmitMerge : onSubmitReplace,
		})
	)(FilterBuilderForm);

const AutoSearchFilterBuilderInner = createFilterBuilderInner(false);
const SubmitOnlyFilterBuilderInner = createFilterBuilderInner(true);

/**
 * @param {{ children: React.ReactNode, disableAutoSearch?: boolean }} props
 */
const FilterBuilder = ({ children, disableAutoSearch = false }) => {
	const gridName = useGridContext();
	const gridFilter = useSelector((state) => getFilter(state, gridName) || {});
	const initialValues = disableAutoSearch
		? pickHeaderFilterValues(gridFilter)
		: gridFilter;
	const FilterBuilderInner = disableAutoSearch
		? SubmitOnlyFilterBuilderInner
		: AutoSearchFilterBuilderInner;
	return (
		<FilterBuilderInner gridName={gridName} initialValues={initialValues}>
			{children}
		</FilterBuilderInner>
	);
};

export default FilterBuilder;
